/**
 * Job stats component
 * Statistics display for job details page
 */

import { useMemo } from 'react';
import { Row, Col, Card, Statistic, Progress, Tooltip, Space, Typography } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { Job, JobProgress } from '@/types/job';
import { formatNumber, formatDuration } from '@/utils/formatters';

const { Text } = Typography;

interface JobStatsProps {
  job: Job;
  progress?: JobProgress;
  loading?: boolean;
}

export function JobStats({ job, progress, loading }: JobStatsProps) {
  // Calculate accurate cell counts from the cells array (not from job.failedCells which can be buggy)
  const cellCounts = useMemo(() => {
    if (!job.cells) {
      return { completed: job.completedCells ?? 0, failed: job.failedCells ?? 0, pending: 0, running: 0 };
    }
    const counts = { completed: 0, failed: 0, pending: 0, running: 0, queued: 0 };
    job.cells.forEach((cell: { status: string }) => {
      const status = cell.status === 'in_progress' ? 'running' : cell.status;
      if (status in counts) {
        counts[status as keyof typeof counts]++;
      }
    });
    return counts;
  }, [job.cells, job.completedCells, job.failedCells]);

  const completedCells = progress?.cellsCompleted ?? cellCounts.completed;
  const failedCells = progress?.cellsFailed ?? cellCounts.failed;
  const pendingCells = cellCounts.pending + cellCounts.queued + cellCounts.running;
  // API returns totalPlaces, not placesFound
  const placesFound = progress?.placesFound ?? (job as any).totalPlaces ?? job.totalPlaces ?? 0;
  const totalCells = job.totalCells;

  const progressPercent = totalCells > 0
    ? Math.round((completedCells / totalCells) * 100)
    : 0;

  const successRate = totalCells > 0
    ? Math.round((completedCells / totalCells) * 100)
    : 0;

  const avgPlacesPerCell = completedCells > 0
    ? (placesFound / completedCells).toFixed(1)
    : '0';

  // Estimate remaining time
  const getEstimatedTime = () => {
    if (!job.startedAt || job.status !== 'running') return null;
    if (completedCells === 0) return 'Calculating...';

    const elapsed = Date.now() - new Date(job.startedAt).getTime();
    const avgTimePerCell = elapsed / completedCells;
    const remainingCells = totalCells - completedCells;
    const estimatedRemaining = avgTimePerCell * remainingCells;

    return formatDuration(estimatedRemaining);
  };

  return (
    <Row gutter={[16, 16]}>
      {/* Overall Progress */}
      <Col xs={24} sm={12} lg={6}>
        <Card size="small" loading={loading}>
          <Statistic
            title="Progress"
            value={progressPercent}
            suffix="%"
            prefix={
              <Progress
                type="circle"
                percent={progressPercent}
                size={40}
                status={
                  job.status === 'failed'
                    ? 'exception'
                    : job.status === 'completed'
                    ? 'success'
                    : 'active'
                }
                format={() => null}
              />
            }
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatNumber(completedCells)} / {formatNumber(totalCells)} cells
          </Text>
        </Card>
      </Col>

      {/* Places Found */}
      <Col xs={24} sm={12} lg={6}>
        <Card size="small" loading={loading}>
          <Statistic
            title="Unique Places"
            value={placesFound}
            prefix={<EnvironmentOutlined style={{ color: '#722ed1' }} />}
            formatter={(val) => formatNumber(val as number)}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            ~{avgPlacesPerCell} unique per cell
          </Text>
        </Card>
      </Col>

      {/* Cell Status */}
      <Col xs={24} sm={12} lg={6}>
        <Card size="small" loading={loading}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text type="secondary">Cell Status</Text>
            <Space size={16}>
              <Tooltip title="Completed cells">
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong>{formatNumber(completedCells)}</Text>
                </Space>
              </Tooltip>
              <Tooltip title="Failed cells">
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Text strong>{formatNumber(failedCells)}</Text>
                </Space>
              </Tooltip>
              <Tooltip title="Pending cells">
                <Space>
                  <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                  <Text strong>{formatNumber(pendingCells)}</Text>
                </Space>
              </Tooltip>
            </Space>
            <Progress
              percent={100}
              success={{ percent: successRate }}
              strokeColor="#ff4d4f"
              showInfo={false}
              size="small"
            />
          </Space>
        </Card>
      </Col>

      {/* Performance */}
      <Col xs={24} sm={12} lg={6}>
        <Card size="small" loading={loading}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text type="secondary">Performance</Text>
            {job.status === 'running' ? (
              <>
                <Space>
                  <ThunderboltOutlined style={{ color: '#faad14' }} />
                  <Text>Est. remaining: {getEstimatedTime() || '-'}</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {progress?.currentRate
                    ? `${progress.currentRate.toFixed(1)} cells/min`
                    : 'Calculating rate...'}
                </Text>
              </>
            ) : job.status === 'completed' ? (
              <>
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text>Success rate: {successRate}%</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatNumber(placesFound)} total places
                </Text>
              </>
            ) : (
              <Text type="secondary">Job not started</Text>
            )}
          </Space>
        </Card>
      </Col>
    </Row>
  );
}

/**
 * Compact stats for smaller displays
 */
export function JobStatsCompact({ job }: { job: Job }) {
  const progressPercent = job.totalCells > 0
    ? Math.round((job.completedCells / job.totalCells) * 100)
    : 0;

  return (
    <Space size={24} wrap>
      <Statistic
        title="Progress"
        value={progressPercent}
        suffix="%"
        valueStyle={{ fontSize: 20 }}
      />
      <Statistic
        title="Places"
        value={job.placesFound}
        formatter={(val) => formatNumber(val as number)}
        valueStyle={{ fontSize: 20 }}
      />
      <Statistic
        title="Cells"
        value={`${job.completedCells}/${job.totalCells}`}
        valueStyle={{ fontSize: 20 }}
      />
    </Space>
  );
}

export default JobStats;
